import { FlashbarProps } from '@cloudscape-design/components';
import { useCallback, useId, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type Resource = Record<string, any> & {
  id: string;
  header: string;
  content?: string;
};

export default function useNotifications() {
  const deletingFlashMessageId = useId();
  const [notifications, setNotifications] = useState<any[]>([]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((notifications) =>
      notifications.filter((notification) => notification.id !== id),
    );
  }, []);

  const updateOrAdd = useCallback(
    (notification: FlashbarProps.MessageDefinition) => {
      setNotifications((notifications) => {
        const existingItemIndex = notifications.findIndex(
          (item) => item.id === notification.id,
        );
        if (existingItemIndex === -1) {
          // Notification with the same id does not exist, add it
          return [notification, ...notifications];
        } else {
          // Notification with the same id already exists, update it
          const newArray = [...notifications];
          newArray.splice(existingItemIndex, 1, notification);
          return newArray;
        }
      });
    },
    [],
  );

  const notifyDeletedOrUpdated = useCallback(
    (resource: FlashbarProps.MessageDefinition) => {
      if (resource) {
        const messageId = `delete-notification-${uuidv4}`;
        updateOrAdd({
          ...resource,
          id: messageId,
          onDismiss: () => dismissNotification(messageId),
        });
      }
    },
    [dismissNotification, updateOrAdd],
  );

  const notifyFailed = useCallback(
    (failedResource: FlashbarProps.MessageDefinition) => {
      if (failedResource) {
        setNotifications((notifications) => [
          {
            ...failedResource,
            type: 'error' as FlashbarProps.Type,
            dismissible: true,
            statusIconAriaLabel: 'error',
            dismissLabel: 'Dismiss message',
            id: failedResource.id,
            onDismiss: () => dismissNotification(failedResource.id ?? ''),
          },
          ...notifications,
        ]);
      }
    },
    [dismissNotification],
  );

  const clearFailed = (resource: FlashbarProps.MessageDefinition) => {
    setNotifications((notifications) =>
      notifications.filter(
        (notification) => notification.id !== `failed-${resource.id}`,
      ),
    );
  };

  const notifyInProgress = useCallback(
    (resource?: FlashbarProps.MessageDefinition) => {
      if (resource) {
        updateOrAdd({
          ...resource,
          loading: true,
          type: 'info',
          statusIconAriaLabel: 'info',
          dismissible: false,
          id: deletingFlashMessageId,
        });
      } else {
        setNotifications((notifications) =>
          notifications.filter(
            (notification) => notification.id !== deletingFlashMessageId,
          ),
        );
      }
    },
    [deletingFlashMessageId, updateOrAdd],
  );

  return {
    clearFailed,
    notifications,
    notifyDeletedOrUpdated,
    notifyFailed,
    notifyInProgress,
    dismissNotification,
    updateOrAdd,
  };
}
